import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler,
} from "chart.js";
import { Line } from "react-chartjs-2";

import api from "../../services/api";
import downloadFile from "../../services/downloadFile";
import { qlAlert, qlConfirm } from "../../utils/dialogs";
import DocumentViewer from "../DocumentViewer";
import { imprimerDocument } from "../../services/imprimer";
import {
  CATEGORIES, COMPTES, ETATS_JUSTIFICATIF, NATURES, STATUTS_DEPENSE, STATUTS_SALAIRE,
  bornesPeriode, correspond, ecrirePeriode, estManuel, fcfa, fmt, fmtDate, libelleJustificatif, libelleMethode,
  libelleMois, lireFocus, lirePeriode, moisCourant, moisCourt, ouvrirPage, parametresPeriode, repartir, sommer,
} from "./comptaOutils";
import "./ComptaEspace.css";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler);

const COULEURS = { revenus: "#22c55e", salaires: "#f59e0b", depenses: "#ef4444", navy: "#203e60" };
const libelles = (paires) => Object.fromEntries(paires);
const LIB_NATURES = libelles(NATURES);
const LIB_CATEGORIES = libelles(CATEGORIES);
const TON_STATUT_DEPENSE = { brouillon: "grey", a_valider: "violet", approuvee: "amber", payee: "green" };
const MULTIPART = { headers: { "Content-Type": "multipart/form-data" } };

function erreurApi(err, defaut = "Une erreur est survenue.") {
  return err?.response?.data?.detail || defaut;
}

function formulaire(valeurs, fichier) {
  const fd = new FormData();
  Object.entries(valeurs).forEach(([cle, valeur]) => {
    if (valeur !== undefined && valeur !== null) fd.append(cle, valeur);
  });
  if (fichier) fd.append("justificatif", fichier);
  return fd;
}

function pdfOuvrir(blob, filename) {
  return downloadFile({
    url: `${api.defaults.baseURL}generated/${encodeURIComponent(filename)}`,
    filename, mimeType: "application/pdf", data: blob,
  }).then((r) => { if (!r?.cancelled) qlAlert("PDF téléchargé avec succès.", "success"); })
    .catch(() => qlAlert("Téléchargement du PDF impossible.", "error"));
}

// ── Éléments communs ─────────────────────────────────────────────────────────

function usePeriode() {
  const [periode, setPeriodeBrute] = useState(lirePeriode);
  const setPeriode = useCallback((suivante) => {
    setPeriodeBrute((p) => {
      const valeur = typeof suivante === "function" ? suivante(p) : suivante;
      ecrirePeriode(valeur);
      return valeur;
    });
  }, []);
  return [periode, setPeriode];
}

function PeriodeBar({ periode, onChange }) {
  const annees = [];
  for (let a = new Date().getFullYear(); a >= 2024; a -= 1) annees.push(String(a));
  return (
    <div className="qc-periode" aria-label="Période">
      <i className="bi bi-calendar3" style={{ color: "#697b90" }}></i>
      <select value={periode.mode} onChange={(e) => onChange({ ...periode, mode: e.target.value })} aria-label="Type de période">
        <option value="mois">Mois</option>
        <option value="annee">Année</option>
        <option value="intervalle">Intervalle</option>
        <option value="tout">Toutes périodes</option>
      </select>
      {periode.mode === "mois" && (
        <input type="month" value={periode.mois} onChange={(e) => onChange({ ...periode, mois: e.target.value || moisCourant() })} aria-label="Mois" />
      )}
      {periode.mode === "annee" && (
        <select value={periode.annee} onChange={(e) => onChange({ ...periode, annee: e.target.value })} aria-label="Année">
          {annees.map((a) => <option key={a}>{a}</option>)}
        </select>
      )}
      {periode.mode === "intervalle" && (
        <>
          <label>du</label>
          <input type="date" value={periode.debut} onChange={(e) => onChange({ ...periode, debut: e.target.value })} aria-label="Début" />
          <label>au</label>
          <input type="date" value={periode.fin} onChange={(e) => onChange({ ...periode, fin: e.target.value })} aria-label="Fin" />
        </>
      )}
    </div>
  );
}

export function Entete({ titre, sousTitre, children }) {
  return (
    <div className="qc-head">
      <div>
        <h1 className="qc-title">{titre}</h1>
        <p className="qc-subtitle">{sousTitre}</p>
      </div>
      {children}
    </div>
  );
}

export function Kpi({ label, value, sub, ton }) {
  return (
    <div className="qc-card qc-kpi" style={{ "--qc-tone": ton }}>
      <div className="qc-kpi-label">{label}</div>
      <div className="qc-kpi-value" title={String(value)}>{value}</div>
      <div className="qc-kpi-sub">{sub}</div>
    </div>
  );
}

export function Ligne({ label, valeur, classe, onClick }) {
  return (
    <div className={`qc-row${onClick ? " qc-row--click" : ""}`} onClick={onClick} role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined} onKeyDown={onClick ? (e) => { if (e.key === "Enter") onClick(); } : undefined}>
      <span>{label}</span><strong className={classe}>{valeur}</strong>
    </div>
  );
}

function Repartition({ lignes, couleur, vide = "Aucune donnée sur la période." }) {
  const total = sommer(lignes, "total");
  if (!lignes.length || !total) return <div className="qc-empty">{vide}</div>;
  return lignes.map((l) => (
    <div key={l.cle || "vide"}>
      <Ligne label={l.label} valeur={fcfa(l.total)} />
      <div className="qc-track"><div className="qc-fill" style={{ width: `${(l.total / total) * 100}%`, background: couleur }} /></div>
    </div>
  ));
}

export function Chip({ ton = "", children }) {
  return <span className={`qc-chip${ton ? ` qc-chip--${ton}` : ""}`}>{children}</span>;
}

export function Chargement() {
  return <div className="qc-empty"><div className="spinner-border text-primary" role="status"></div></div>;
}

export function Fenetre({ titre, onClose, children }) {
  useEffect(() => {
    const fermer = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", fermer);
    return () => document.removeEventListener("keydown", fermer);
  }, [onClose]);
  return (
    <div className="qc-modal-back" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="qc-modal" role="dialog" aria-modal="true" aria-label={titre}>
        <div className="qc-modal-head">
          <h3>{titre}</h3>
          <button type="button" className="qc-btn qc-btn--sm" onClick={onClose}><i className="bi bi-x-lg"></i>Fermer</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Historique({ type, id, rafraichir }) {
  const [lignes, setLignes] = useState(null);
  useEffect(() => {
    if (!id) return;
    api.get(`compta/historique/${type}/${id}/`).then((r) => setLignes(r.data.historique || [])).catch(() => setLignes([]));
  }, [type, id, rafraichir]);
  return (
    <>
      <div className="qc-section-title">Historique des modifications</div>
      {lignes === null ? <Chargement /> : lignes.length === 0 ? (
        <div className="qc-foot">Aucune modification enregistrée depuis la mise en place de l’historique.</div>
      ) : (
        <ul className="qc-histo">
          {lignes.map((l, i) => (
            <li key={i}><strong>{l.action}</strong> <span>— {l.auteur}, {new Date(l.date).toLocaleString("fr-FR")}</span>
              {l.details && <div><span>{l.details}</span></div>}</li>
          ))}
        </ul>
      )}
    </>
  );
}

function Justificatif({ url, etat, titre }) {
  const [ouvert, setOuvert] = useState(false);
  if (url) {
    return (
      <>
        <button type="button" className="qc-link" onClick={() => setOuvert(true)}><i className="bi bi-paperclip me-1"></i>Ouvrir</button>
        {ouvert && <DocumentViewer titre={titre || "Justificatif"} url={url} onClose={() => setOuvert(false)} />}
      </>
    );
  }
  const ton = etat === "manquant" ? "red" : etat === "non_requis" ? "grey" : "";
  return ton ? <Chip ton={ton}>{libelleJustificatif(etat)}</Chip> : <span className="qc-foot">Non renseigné</span>;
}

function ChampsJustificatif({ etat, onEtat, onFichier, existant }) {
  return (
    <>
      <label>Justificatif
        <select value={etat} onChange={(e) => onEtat(e.target.value)}>
          <option value="">Non renseigné</option>
          {ETATS_JUSTIFICATIF.filter(([cle]) => cle !== "joint").map(([cle, label]) => <option key={cle} value={cle}>{label}</option>)}
          {existant && <option value="joint">Joint</option>}
        </select>
      </label>
      <label>Joindre une pièce (PDF ou image)
        <input type="file" accept="application/pdf,image/*" onChange={(e) => onFichier(e.target.files?.[0] || null)} />
      </label>
    </>
  );
}

export function Message({ texte }) {
  if (!texte) return null;
  return <div className={`qc-msg ${texte.startsWith("✓") ? "qc-msg--ok" : "qc-msg--err"}`}>{texte}</div>;
}

// ── Argent disponible (indépendant de la période) ───────────────────────────

const COMPTES_TRESORERIE = [["caisse", "Caisse"], ["mtn_momo", "MTN MoMo"], ["orange_money", "Orange Money"], ["banque", "Banque"], ["autre", "Autre"]];

function SaisieSolde({ onClose, onSaisi }) {
  const [valeurs, setValeurs] = useState({ date: new Date().toISOString().slice(0, 10), note: "" });
  const [message, setMessage] = useState("");
  const [envoi, setEnvoi] = useState(false);
  const total = COMPTES_TRESORERIE.reduce((t, [cle]) => t + (parseInt(String(valeurs[cle] || "0").replace(/\s/g, ""), 10) || 0), 0);
  const envoyer = (e) => {
    e.preventDefault();
    setEnvoi(true);
    api.post("compta/tresorerie/", valeurs)
      .then((r) => { onSaisi(r.data.tresorerie); onClose(); })
      .catch((err) => setMessage(erreurApi(err, "Enregistrement impossible.")))
      .finally(() => setEnvoi(false));
  };
  return (
    <Fenetre titre="Argent réellement disponible" onClose={onClose}>
      <form className="qc-form" onSubmit={envoyer}>
        <div className="qc-full qc-note">Comptez l’argent qui se trouve réellement dans chaque compte. Date d’aujourd’hui : le montant
          compté maintenant ; seules les opérations enregistrées ensuite seront ajoutées ou retirées. Date passée : le montant
          à la fin de cette journée ; les opérations des jours suivants seront ajoutées ou retirées.</div>
        <label>Date du constat
          <input type="date" required value={valeurs.date} max={new Date().toISOString().slice(0, 10)}
            onChange={(e) => setValeurs({ ...valeurs, date: e.target.value })} />
        </label>
        {COMPTES_TRESORERIE.map(([cle, lib]) => (
          <label key={cle}>{lib} (FCFA)
            <input type="number" min="0" step="1" inputMode="numeric" value={valeurs[cle] ?? ""} placeholder="0"
              onChange={(e) => setValeurs({ ...valeurs, [cle]: e.target.value })} />
          </label>
        ))}
        <label className="qc-full">Remarque (facultatif)
          <input type="text" maxLength={300} value={valeurs.note} onChange={(e) => setValeurs({ ...valeurs, note: e.target.value })} />
        </label>
        <div className="qc-full qc-tres-total">Total constaté : <strong>{fcfa(total)}</strong></div>
        <Message texte={message} />
        <div className="qc-full qc-actions">
          <button type="button" className="qc-btn" onClick={onClose}>Annuler</button>
          <button type="submit" className="qc-btn qc-btn--primary" disabled={envoi}>Enregistrer</button>
        </div>
      </form>
    </Fenetre>
  );
}

export function TresorerieCarte() {
  const [etat, setEtat] = useState(null);
  const [saisie, setSaisie] = useState(false);
  const [detail, setDetail] = useState(false);
  const charger = useCallback(() => {
    api.get("compta/tresorerie/").then((r) => setEtat(r.data)).catch(() => setEtat({ erreur: true }));
  }, []);
  useEffect(() => { charger(); const id = setInterval(charger, 60000); return () => clearInterval(id); }, [charger]);
  if (!etat || etat.erreur) return null;
  const t = etat.tresorerie;
  return (
    <div className="qc-card qc-tres">
      <div className="qc-tres-tete">
        <div>
          <div className="qc-tres-label"><i className="bi bi-safe2 me-2"></i>Argent disponible aujourd’hui</div>
          {t ? (
            <>
              <div className={`qc-tres-valeur${t.disponible < 0 ? " qc-tres-valeur--neg" : ""}`}>{fcfa(t.disponible)}</div>
              <div className="qc-tres-sub">Toutes périodes confondues : ne revient pas à zéro au changement de mois.
                {t.depart === "remise"
                  ? ` Point de départ : remise à zéro comptable du ${fmtDate(t.date)}.`
                  : ` Point de départ : ${fcfa(t.solde_initial)} constatés le ${fmtDate(t.date)}.`}</div>
            </>
          ) : (
            <div className="qc-tres-sub">Saisissez une fois l’argent réellement disponible (caisse, MTN MoMo, Orange Money, banque) :
              le montant sera ensuite tenu à jour en permanence avec les encaissements, salaires et dépenses.</div>
          )}
        </div>
        <div className="qc-tres-actions">
          {t && <button type="button" className="qc-btn qc-btn--sm" onClick={() => setDetail((d) => !d)}>
            <i className={`bi bi-chevron-${detail ? "up" : "down"}`}></i>Détail</button>}
          <button type="button" className="qc-btn qc-btn--sm qc-btn--primary" onClick={() => setSaisie(true)}>
            <i className="bi bi-pencil-square"></i>{t ? "Recaler le solde" : "Saisir le solde de départ"}</button>
        </div>
      </div>
      {t && detail && (
        <div className="qc-tres-detail">
          <div>
            <div className="qc-section-title">Calcul</div>
            <Ligne label={t.depart === "remise" ? `Remise à zéro du ${fmtDate(t.date)}` : `Solde constaté le ${fmtDate(t.date)}`}
              valeur={fcfa(t.solde_initial)} />
            <Ligne label="+ Revenus encaissés depuis" valeur={fcfa(t.encaissements)} />
            {t.apports ? <Ligne label="+ Apports encaissés depuis" valeur={fcfa(t.apports)} /> : null}
            <Ligne label="− Salaires versés depuis" valeur={fcfa(t.salaires)} />
            <Ligne label="− Dépenses payées depuis" valeur={fcfa(t.depenses)} />
            <Ligne label="= Argent disponible" valeur={fcfa(t.disponible)} />
          </div>
          <div>
            {t.comptes.length > 0 && <div className="qc-section-title">Répartition au {fmtDate(t.date)}</div>}
            {t.comptes.filter((c) => c.montant).map((c) => <Ligne key={c.cle} label={c.label} valeur={fcfa(c.montant)} />)}
            {t.note && <div className="qc-foot">{t.note}</div>}
            {etat.historique.length > 1 && (
              <>
                <div className="qc-section-title">Soldes saisis</div>
                {etat.historique.map((h, i) => <Ligne key={i} label={`${fmtDate(h.date)}${h.saisi_par ? ` · ${h.saisi_par}` : ""}`} valeur={fcfa(h.total)} />)}
              </>
            )}
          </div>
        </div>
      )}
      {saisie && <SaisieSolde onClose={() => setSaisie(false)} onSaisi={() => charger()} />}
    </div>
  );
}

// ── Tableau de bord ─────────────────────────────────────────────────────────

export function ComptaDashboardPage() {
  const [periode, setPeriode] = usePeriode();
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState("");
  const { label } = bornesPeriode(periode);

  useEffect(() => {
    let actif = true;
    const charger = () => api.get(`compta/dashboard/${parametresPeriode(periode)}`)
      .then((r) => { if (actif) { setData(r.data); setErreur(""); } })
      .catch((err) => { if (actif) setErreur(erreurApi(err, "Tableau de bord indisponible.")); });
    charger();
    const id = setInterval(charger, 60000);
    return () => { actif = false; clearInterval(id); };
  }, [periode]);

  const serie = useMemo(() => data?.serie_mensuelle || [], [data]);
  const graphique = useMemo(() => ({
    labels: serie.map((m) => moisCourt(m.mois)),
    datasets: [
      { label: "Revenus", data: serie.map((m) => m.revenus / 1000), borderColor: COULEURS.revenus, backgroundColor: COULEURS.revenus, tension: .25, pointRadius: 4 },
      { label: "Salaires versés", data: serie.map((m) => m.salaires / 1000), borderColor: COULEURS.salaires, backgroundColor: COULEURS.salaires, tension: .25, pointRadius: 4, borderDash: [6, 3] },
      { label: "Autres dépenses", data: serie.map((m) => m.depenses / 1000), borderColor: COULEURS.depenses, backgroundColor: COULEURS.depenses, tension: .25, pointRadius: 4, borderDash: [2, 3] },
    ],
  }), [serie]);

  return (
    <div className="qc">
      <Entete titre="Tableau de bord comptable" sousTitre="Revenus, salaires et dépenses réunis dans une vue claire.">
        <PeriodeBar periode={periode} onChange={setPeriode} />
      </Entete>
      {erreur && <div className="alert alert-danger">{erreur}</div>}
      <TresorerieCarte />
      {!data ? <Chargement /> : (
        <>
          <div className="qc-kpis">
            <Kpi label="Revenus enregistrés" value={fmt(data.revenus)} ton={COULEURS.revenus}
              sub={data.apports ? `FCFA · ${label} · + ${fmt(data.apports)} d’apports (hors revenus)` : `FCFA · ${label}`} />
            <Kpi label="Salaires versés" value={fmt(data.total_salaires)} ton={COULEURS.salaires} sub="FCFA · date effective du versement" />
            <Kpi label="Dépenses hors salaires" value={fmt(data.total_depenses)} ton={COULEURS.depenses} sub="FCFA · dépenses payées" />
            <Kpi label="Solde des opérations" value={fmt(data.solde)} ton={data.solde < 0 ? COULEURS.depenses : COULEURS.navy} sub="FCFA · revenus − sorties" />
          </div>

          <div className="qc-cols">
            <div className="qc-card">
              <h3>Évolution des opérations</h3>
              <div className="qc-legend">
                <span><b className="qc-dot" style={{ background: COULEURS.revenus }}></b>Revenus</span>
                <span><b className="qc-dot" style={{ background: COULEURS.salaires }}></b>Salaires versés</span>
                <span><b className="qc-dot" style={{ background: COULEURS.depenses }}></b>Autres dépenses</span>
              </div>
              <div className="qc-chart">
                <Line data={graphique} options={{
                  responsive: true, maintainAspectRatio: false,
                  plugins: { legend: { display: false }, tooltip: { callbacks: { label: (c) => `${c.dataset.label} : ${fcfa(c.parsed.y * 1000)}` } } },
                  scales: { y: { beginAtZero: true, title: { display: true, text: "Milliers de FCFA" }, grid: { color: "#eef2f6" } }, x: { grid: { display: false } } },
                }} />
              </div>
            </div>
            <div className="qc-card">
              <h3>À traiter en priorité</h3>
              {data.alertes.length === 0 ? <div className="qc-empty">Rien à signaler sur cette période.</div> : data.alertes.map((a) => (
                <Ligne key={a.code} label={a.libelle} onClick={() => ouvrirPage(a.page, a.filtre)}
                  classe={a.niveau === "rouge" ? "qc-red" : a.niveau === "orange" ? "qc-amber" : a.niveau === "violet" ? "" : "qc-navy"}
                  valeur={a.montant ? `${fmt(a.valeur)} FCFA` : `${a.valeur}`} />
              ))}
              <div className="qc-foot">Cliquez sur une alerte pour ouvrir les opérations concernées.</div>
            </div>
          </div>

          <div className="qc-cols qc-cols--half">
            <div className="qc-card">
              <h3>Répartition des revenus</h3>
              <Repartition couleur={COULEURS.revenus}
                lignes={data.revenus_par_methode.map((l) => ({ ...l, label: libelleMethode(l.cle) }))} />
              {data.revenus_par_nature.some((n) => n.cle) && (
                <>
                  <div className="qc-section-title">Par nature</div>
                  <Repartition couleur={COULEURS.navy} lignes={data.revenus_par_nature} />
                </>
              )}
            </div>
            <div className="qc-card">
              <h3>Dépenses par catégorie</h3>
              <Repartition couleur={COULEURS.depenses} lignes={data.depenses_par_categorie} vide="Aucune dépense payée sur la période." />
            </div>
          </div>

          <div className="qc-card">
            <h3>Dernières opérations</h3>
            {data.dernieres_operations.length === 0 ? <div className="qc-empty">Aucune opération sur la période.</div> : (
              <div className="qc-table-wrap">
                <table className="qc-table">
                  <thead><tr><th>Date</th><th>Type</th><th>Libellé</th><th className="qc-num">Montant</th></tr></thead>
                  <tbody>
                    {data.dernieres_operations.map((o) => (
                      <tr key={`${o.type}-${o.id}`} style={{ cursor: "pointer" }}
                        onClick={() => ouvrirPage(`compta_${o.type === "revenu" ? "revenus" : o.type === "salaire" ? "salaires" : "depenses"}`, { ouvrir: o.id })}>
                        <td>{fmtDate(o.date)}</td>
                        <td>{o.type === "revenu" ? <Chip ton="green">{o.apport ? "Apport" : "Revenu"}</Chip>
                          : o.type === "salaire" ? <Chip ton="amber">Salaire</Chip> : <Chip ton="red">Dépense</Chip>}</td>
                        <td>{o.libelle}</td>
                        <td className={`qc-num ${o.type === "revenu" ? "qc-green" : o.type === "salaire" ? "qc-amber" : "qc-red"}`}>
                          {o.type === "revenu" ? "+" : "−"} {fmt(o.montant)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div className="qc-foot">Le solde des opérations n’est ni le bénéfice comptable ni le solde bancaire réel.
            {data.apports ? " Les apports sont des encaissements, pas des revenus : ils n’entrent pas dans ce solde." : ""}</div>
        </>
      )}
    </div>
  );
}

// ── Revenus ─────────────────────────────────────────────────────────────────

const FORM_MANUEL = { montant: "", date: "", methode: "MANUEL", nature: "", compte: "", description: "", user_id: "", justificatif_etat: "" };

function FormPaiementManuel({ onClose, onSaved }) {
  const [form, setForm] = useState({ ...FORM_MANUEL, date: new Date().toISOString().slice(0, 10) });
  const [fichier, setFichier] = useState(null);
  const [recherche, setRecherche] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [eleve, setEleve] = useState("");
  const [msg, setMsg] = useState("");
  const [envoi, setEnvoi] = useState(false);
  const maj = (cle) => (e) => setForm((f) => ({ ...f, [cle]: e.target.value }));

  useEffect(() => {
    if (recherche.length < 2) { setSuggestions([]); return undefined; }
    const id = setTimeout(() => {
      api.get(`admin/users/?search=${encodeURIComponent(recherche)}&per_page=5`)
        .then((r) => setSuggestions(r.data.users || [])).catch(() => setSuggestions([]));
    }, 250);
    return () => clearTimeout(id);
  }, [recherche]);

  const envoyer = async (e) => {
    e.preventDefault();
    setEnvoi(true); setMsg("");
    try {
      const r = await api.post("compta/paiement-manuel/", formulaire(form, fichier), MULTIPART);
      onSaved(`✓ Paiement de ${fmt(r.data.montant)} FCFA enregistré. Réf : ${r.data.reference}`);
    } catch (err) { setMsg(erreurApi(err, "Erreur lors de l'enregistrement.")); }
    setEnvoi(false);
  };

  return (
    <Fenetre titre="Paiement manuel · fiche de saisie" onClose={onClose}>
      <form className="qc-form" onSubmit={envoyer}>
        <label>Montant (FCFA) *<input type="number" min="1" required value={form.montant} onChange={maj("montant")} /></label>
        <label>Date de l’encaissement<input type="date" value={form.date} max={new Date().toISOString().slice(0, 10)} onChange={maj("date")} /></label>
        <label>Méthode de saisie
          <select value={form.methode} onChange={maj("methode")}>
            <option value="MANUEL">Paiement manuel</option>
            <option value="GENERATEUR_EPREUVES">Portefeuille enseignant</option>
            <option value="code_manuel">Vente manuel (code)</option>
            <option value="FINANCEMENT">Financement</option>
            <option value="SOUTIEN">Soutien</option>
          </select>
        </label>
        <label>Nature de l’entrée
          <select value={form.nature} onChange={maj("nature")}>
            <option value="">Non renseignée</option>
            {NATURES.map(([cle, lib]) => <option key={cle} value={cle}>{lib}</option>)}
          </select>
        </label>
        <label>Compte de destination
          <select value={form.compte} onChange={maj("compte")}>
            <option value="">Non renseigné</option>
            {COMPTES.map(([cle, lib]) => <option key={cle} value={cle}>{lib}</option>)}
          </select>
        </label>
        <label style={{ position: "relative" }}>Élève / utilisateur (optionnel)
          {eleve ? (
            <span className="qc-chip qc-chip--green" style={{ justifyContent: "space-between", padding: "9px 12px" }}>{eleve}
              <button type="button" className="qc-link" onClick={() => { setEleve(""); setForm((f) => ({ ...f, user_id: "" })); }}>Retirer</button>
            </span>
          ) : <input placeholder="Taper un nom…" value={recherche} onChange={(e) => setRecherche(e.target.value)} />}
          {suggestions.length > 0 && !eleve && (
            <div className="qc-suggest">
              {suggestions.map((u) => (
                <div key={u.id} onMouseDown={(e) => { e.preventDefault(); setForm((f) => ({ ...f, user_id: u.id })); setEleve(u.nom_complet || u.email); setSuggestions([]); setRecherche(""); }}>
                  <strong>{u.nom_complet}</strong><br /><small>{u.email}</small>
                </div>
              ))}
            </div>
          )}
        </label>
        <label className="qc-full">Description<input value={form.description} onChange={maj("description")} placeholder="Ex. : 12 manuels — établissement partenaire" /></label>
        <ChampsJustificatif etat={form.justificatif_etat} onEtat={(v) => setForm((f) => ({ ...f, justificatif_etat: v }))} onFichier={setFichier} />
        <div className="qc-full qc-note">« Paiement manuel » est une méthode de saisie : précisez la nature (abonnement, vente de manuels…). Un apport n’est pas compté comme revenu.</div>
        <div className="qc-actions qc-full">
          <button type="submit" className="qc-btn qc-btn--primary" disabled={envoi}>{envoi ? "Enregistrement…" : "Enregistrer"}</button>
          <button type="button" className="qc-btn" onClick={onClose}>Annuler</button>
        </div>
        <Message texte={msg} />
      </form>
    </Fenetre>
  );
}

function FicheRevenu({ revenu, onClose, onChange }) {
  const [form, setForm] = useState({
    nature: revenu.nature || "", compte: revenu.compte || "", description: revenu.description || "",
    justificatif_etat: revenu.justificatif_etat || "",
  });
  const [manuel, setManuel] = useState({ montant: revenu.montant, methode: revenu.methode });
  const [fichier, setFichier] = useState(null);
  const [msg, setMsg] = useState("");
  const [version, setVersion] = useState(0);
  const maj = (cle) => (e) => setForm((f) => ({ ...f, [cle]: e.target.value }));

  const enregistrer = async () => {
    setMsg("");
    try {
      if (estManuel(revenu) && (Number(manuel.montant) !== revenu.montant || manuel.methode !== revenu.methode)) {
        await api.patch(`compta/paiement-manuel/${revenu.id}/`, manuel);
      }
      await api.patch(`compta/revenus/${revenu.id}/fiche/`, formulaire(form, fichier), MULTIPART);
      setMsg("✓ Fiche enregistrée."); setVersion((v) => v + 1); onChange();
    } catch (err) { setMsg(erreurApi(err)); }
  };
  const supprimer = () => qlConfirm("Supprimer ce paiement manuel ? Cette action est irréversible.", async () => {
    try { await api.delete(`compta/paiement-manuel/${revenu.id}/`); onChange(); onClose(); }
    catch (err) { qlAlert(erreurApi(err), "error"); }
  }, { danger: true, okLabel: "Supprimer" });

  return (
    <Fenetre titre="Détail du revenu" onClose={onClose}>
      <div className="qc-detailgrid">
        <Ligne label="Date" valeur={fmtDate(revenu.date)} />
        <Ligne label="Montant" valeur={fcfa(revenu.montant)} classe="qc-green" />
        <Ligne label="Élève / libellé" valeur={revenu.nom} />
        <Ligne label="Méthode" valeur={libelleMethode(revenu.methode)} />
        <Ligne label="Référence" valeur={revenu.reference} />
        <Ligne label="Justificatif" valeur={<Justificatif url={revenu.justificatif_url} etat={revenu.justificatif_etat} titre={`Justificatif ${revenu.reference}`} />} />
      </div>
      <div className="qc-section-title">Informations comptables</div>
      <div className="qc-form">
        {estManuel(revenu) && (
          <>
            <label>Montant (paiement manuel)<input type="number" min="1" value={manuel.montant} onChange={(e) => setManuel((m) => ({ ...m, montant: e.target.value }))} /></label>
            <label>Méthode de saisie
              <select value={manuel.methode} onChange={(e) => setManuel((m) => ({ ...m, methode: e.target.value }))}>
                <option value="MANUEL">Paiement manuel</option>
                <option value="GENERATEUR_EPREUVES">Portefeuille enseignant</option>
                <option value="code_manuel">Vente manuel (code)</option>
                <option value="FINANCEMENT">Financement</option>
                <option value="SOUTIEN">Soutien</option>
              </select>
            </label>
          </>
        )}
        <label>Nature de l’entrée
          <select value={form.nature} onChange={maj("nature")}>
            <option value="">Non renseignée</option>
            {NATURES.map(([cle, lib]) => <option key={cle} value={cle}>{lib}</option>)}
          </select>
        </label>
        <label>Compte de destination
          <select value={form.compte} onChange={maj("compte")}>
            <option value="">Non renseigné</option>
            {COMPTES.map(([cle, lib]) => <option key={cle} value={cle}>{lib}</option>)}
          </select>
        </label>
        <label className="qc-full">Description<input value={form.description} onChange={maj("description")} /></label>
        <ChampsJustificatif etat={form.justificatif_etat} existant={Boolean(revenu.justificatif_url)} onEtat={(v) => setForm((f) => ({ ...f, justificatif_etat: v }))} onFichier={setFichier} />
      </div>
      <div className="qc-actions">
        <button type="button" className="qc-btn qc-btn--primary" onClick={enregistrer}><i className="bi bi-check2"></i>Enregistrer</button>
        {estManuel(revenu) && <button type="button" className="qc-btn qc-btn--danger" onClick={supprimer}><i className="bi bi-trash"></i>Supprimer</button>}
      </div>
      <Message texte={msg} />
      <Historique type="revenu" id={revenu.id} rafraichir={version} />
    </Fenetre>
  );
}

export function ComptaRevenusPage() {
  const [periode, setPeriode] = usePeriode();
  const [revenus, setRevenus] = useState(null);
  const [filtres, setFiltres] = useState({ recherche: "", methode: "", nature: "", justificatif: "" });
  const [saisie, setSaisie] = useState(false);
  const [fiche, setFiche] = useState(null);
  const [msg, setMsg] = useState("");
  const [focus] = useState(() => lireFocus("compta_revenus"));
  const { label } = bornesPeriode(periode);

  const charger = useCallback(() => api.get(`compta/revenus/${parametresPeriode(periode)}`)
    .then((r) => setRevenus(r.data.revenus || [])).catch(() => setRevenus([])), [periode]);
  useEffect(() => { charger(); }, [charger]);
  useEffect(() => {
    if (focus?.justificatif) setFiltres((f) => ({ ...f, justificatif: focus.justificatif }));
  }, [focus]);
  useEffect(() => {
    if (focus?.ouvrir && revenus) {
      const r = revenus.find((x) => x.id === focus.ouvrir);
      if (r) setFiche(r);
    }
  }, [focus, revenus]);

  const liste = revenus || [];
  const methodes = [...new Set(liste.map((r) => r.methode))];
  const filtres_ = liste.filter((r) => (!filtres.methode || r.methode === filtres.methode)
    && (!filtres.nature || (filtres.nature === "_vide" ? !r.nature : r.nature === filtres.nature))
    && (!filtres.justificatif || (filtres.justificatif === "_vide" ? !r.justificatif_etat : r.justificatif_etat === filtres.justificatif))
    && correspond(filtres.recherche, r.nom, r.reference, r.description, r.nature_label, libelleMethode(r.methode)));
  const horsApports = liste.filter((r) => r.nature !== "apport");
  const apports = sommer(liste.filter((r) => r.nature === "apport"), "montant");
  const manuels = horsApports.filter(estManuel);
  const autres = horsApports.filter((r) => !estManuel(r));
  const etats = repartir(liste.map((r) => ({ ...r, un: 1 })), "justificatif_etat", "un",
    { joint: "Joints", manquant: "À fournir", non_requis: "Non requis", "": "Non renseignés" });

  return (
    <div className="qc">
      <Entete titre="Revenus" sousTitre="Retrouver chaque entrée, son origine et sa référence.">
        <PeriodeBar periode={periode} onChange={setPeriode} />
      </Entete>
      {revenus === null ? <Chargement /> : (
        <>
          <div className="qc-kpis">
            <Kpi label="Revenus de la période" value={fmt(sommer(horsApports, "montant"))} ton={COULEURS.revenus}
              sub={apports ? `FCFA · ${label} · + ${fmt(apports)} d’apports` : `FCFA · ${label}`} />
            <Kpi label="Paiements manuels" value={fmt(sommer(manuels, "montant"))} ton={COULEURS.navy} sub={`FCFA · ${manuels.length} saisie(s)`} />
            <Kpi label="Paiements en ligne et autres" value={fmt(sommer(autres, "montant"))} ton={COULEURS.navy} sub="FCFA · CinetPay, codes, portefeuille…" />
            <Kpi label="Opérations enregistrées" value={liste.length} ton={COULEURS.navy} sub="Sur la période" />
          </div>
          <div className="qc-toolbar">
            <input className="qc-search" placeholder="Rechercher un élève, une référence…" value={filtres.recherche}
              onChange={(e) => setFiltres((f) => ({ ...f, recherche: e.target.value }))} aria-label="Rechercher" />
            <select value={filtres.methode} onChange={(e) => setFiltres((f) => ({ ...f, methode: e.target.value }))} aria-label="Méthode">
              <option value="">Toutes les méthodes</option>
              {methodes.map((m) => <option key={m} value={m}>{libelleMethode(m)}</option>)}
            </select>
            <select value={filtres.nature} onChange={(e) => setFiltres((f) => ({ ...f, nature: e.target.value }))} aria-label="Nature">
              <option value="">Toutes les natures</option>
              {NATURES.map(([cle, lib]) => <option key={cle} value={cle}>{lib}</option>)}
              <option value="_vide">Non renseignée</option>
            </select>
            <select value={filtres.justificatif} onChange={(e) => setFiltres((f) => ({ ...f, justificatif: e.target.value }))} aria-label="Justificatif">
              <option value="">Tous les justificatifs</option>
              {ETATS_JUSTIFICATIF.map(([cle, lib]) => <option key={cle} value={cle}>{lib}</option>)}
              <option value="_vide">Non renseigné</option>
            </select>
            <span className="qc-spacer" />
            <button type="button" className="qc-btn qc-btn--primary" onClick={() => { setSaisie(true); setMsg(""); }}><i className="bi bi-plus-lg"></i>Paiement manuel</button>
          </div>
          <Message texte={msg} />
          <div className="qc-card" style={{ marginBottom: 16 }}>
            <h3>Revenus enregistrés <span className="qc-foot" style={{ marginLeft: "auto", marginTop: 0, fontWeight: 400 }}>{filtres_.length} opération(s) · {fcfa(sommer(filtres_, "montant"))}</span></h3>
            {filtres_.length === 0 ? <div className="qc-empty">Aucun revenu correspondant.</div> : (
              <div className="qc-table-wrap">
                <table className="qc-table">
                  <thead><tr><th>Date</th><th>Élève / libellé</th><th>Méthode</th><th>Nature</th><th className="qc-num">Montant</th><th>Référence</th><th>Détail</th></tr></thead>
                  <tbody>
                    {filtres_.map((r) => (
                      <tr key={r.id}>
                        <td>{fmtDate(r.date)}</td>
                        <td>{r.nom}{r.description && <small>{r.description}</small>}</td>
                        <td>{libelleMethode(r.methode)}</td>
                        <td>{r.nature ? <Chip ton={r.nature === "apport" ? "violet" : "green"}>{r.nature_label}</Chip> : <span className="qc-foot">Non renseignée</span>}</td>
                        <td className="qc-num qc-green">{fmt(r.montant)}</td>
                        <td style={{ fontSize: ".78rem" }}>{r.reference}</td>
                        <td><button type="button" className="qc-link" onClick={() => setFiche(r)}>Voir</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div className="qc-cols qc-cols--half">
            <div className="qc-card">
              <h3>Nature des recettes</h3>
              <Repartition couleur={COULEURS.revenus} lignes={repartir(liste, "nature", "montant", LIB_NATURES)} />
              <div className="qc-foot">La nature du revenu est distincte du mode de paiement. Les apports ne sont pas des revenus.</div>
            </div>
            <div className="qc-card">
              <h3>Justificatifs</h3>
              {etats.length === 0 ? <div className="qc-empty">Aucune opération.</div> : etats.map((e) => (
                <Ligne key={e.cle || "vide"} label={e.label} valeur={`${e.total}`} classe={e.cle === "manquant" ? "qc-red" : ""}
                  onClick={() => setFiltres((f) => ({ ...f, justificatif: e.cle || "_vide" }))} />
              ))}
              <div className="qc-foot">Une pièce jointe passe automatiquement en « Joint ». « Non renseigné » n’est pas « à fournir ».</div>
            </div>
          </div>
        </>
      )}
      {saisie && <FormPaiementManuel onClose={() => setSaisie(false)} onSaved={(m) => { setSaisie(false); setMsg(m); charger(); }} />}
      {fiche && <FicheRevenu revenu={fiche} onClose={() => setFiche(null)} onChange={charger} />}
    </div>
  );
}

// ── Salaires ────────────────────────────────────────────────────────────────

function FormVersement({ versement, initial, beneficiaires, onClose, onSaved }) {
  const [form, setForm] = useState(() => versement ? {
    beneficiaire: versement.beneficiaire, montant: versement.montant, date_paiement: versement.date_paiement,
    mois_paie: versement.mois_paie || "", compte: versement.compte || "", description: versement.description || "",
    justificatif_etat: versement.justificatif_etat || "",
  } : {
    beneficiaire: "", montant: "", date_paiement: new Date().toISOString().slice(0, 10), mois_paie: moisCourant(),
    compte: "", description: "", justificatif_etat: "", ...initial,
  });
  const [fichier, setFichier] = useState(null);
  const [msg, setMsg] = useState("");
  const [version, setVersion] = useState(0);
  const maj = (cle) => (e) => setForm((f) => ({ ...f, [cle]: e.target.value }));

  const envoyer = async (e) => {
    e.preventDefault(); setMsg("");
    const donnees = { ...form, mois_concerne: form.mois_paie ? libelleMois(form.mois_paie).replace(/^./, (c) => c.toUpperCase()) : "" };
    try {
      if (versement) await api.put(`compta/salaires/${versement.id}/`, formulaire(donnees, fichier), MULTIPART);
      else await api.post("compta/salaires/", formulaire(donnees, fichier), MULTIPART);
      setVersion((v) => v + 1);
      onSaved();
    } catch (err) { setMsg(erreurApi(err)); }
  };
  const nomBulletin = () => `bulletin_${versement.beneficiaire.replace(/ /g, "_")}.pdf`;
  const chargerBulletin = () => api.get(`compta/salaires/${versement.id}/bulletin/`, { responseType: "arraybuffer" })
    .then((r) => new Blob([r.data], { type: "application/pdf" }));
  const bulletin = () => chargerBulletin().then((blob) => pdfOuvrir(blob, nomBulletin()))
    .catch(() => qlAlert("Erreur génération bulletin.", "error"));
  const imprimerBulletin = () => chargerBulletin().then((data) => imprimerDocument({ data, filename: nomBulletin() }))
    .catch(() => qlAlert("Erreur génération bulletin.", "error"));
  const supprimer = () => qlConfirm("Supprimer ce versement ?", async () => {
    try { await api.delete(`compta/salaires/${versement.id}/`); onSaved(); }
    catch (err) { qlAlert(erreurApi(err), "error"); }
  }, { danger: true, okLabel: "Supprimer" });

  return (
    <Fenetre titre={versement ? "Versement de salaire" : "Ajouter un versement de salaire"} onClose={onClose}>
      <form className="qc-form" onSubmit={envoyer}>
        <label>Bénéficiaire *<input list="qc-beneficiaires" required value={form.beneficiaire} onChange={maj("beneficiaire")} /></label>
        <datalist id="qc-beneficiaires">{beneficiaires.map((b) => <option key={b} value={b} />)}</datalist>
        <label>Montant versé (FCFA) *<input type="number" min="1" required value={form.montant} onChange={maj("montant")} /></label>
        <label>Date du paiement *<input type="date" required value={form.date_paiement} onChange={maj("date_paiement")} /></label>
        <label>Mois de paie concerné<input type="month" value={form.mois_paie} onChange={maj("mois_paie")} /></label>
        <label>Compte de paiement
          <select value={form.compte} onChange={maj("compte")}>
            <option value="">Non renseigné</option>
            {COMPTES.map(([cle, lib]) => <option key={cle} value={cle}>{lib}</option>)}
          </select>
        </label>
        <label>Description<input value={form.description} onChange={maj("description")} placeholder="Salaire, avance, complément…" /></label>
        <ChampsJustificatif etat={form.justificatif_etat} existant={Boolean(versement?.justificatif_url)} onEtat={(v) => setForm((f) => ({ ...f, justificatif_etat: v }))} onFichier={setFichier} />
        {versement?.justificatif_url && <div className="qc-full"><Justificatif url={versement.justificatif_url} titre={`Justificatif — ${versement.beneficiaire}`} /></div>}
        <div className="qc-full qc-note">La sortie de trésorerie suit la date du paiement ; le mois de paie rattache ce versement au salaire prévu. Aucun barème fiscal ou social n’est appliqué.</div>
        <div className="qc-actions qc-full">
          <button type="submit" className="qc-btn qc-btn--primary"><i className="bi bi-check2"></i>Enregistrer</button>
          {versement && <button type="button" className="qc-btn" onClick={bulletin}><i className="bi bi-file-earmark-pdf"></i>Bulletin de paie</button>}
          {versement && <button type="button" className="qc-btn" onClick={imprimerBulletin}><i className="bi bi-printer"></i>Imprimer le bulletin</button>}
          {versement && <button type="button" className="qc-btn qc-btn--danger" onClick={supprimer}><i className="bi bi-trash"></i>Supprimer</button>}
          <button type="button" className="qc-btn" onClick={onClose}>Annuler</button>
        </div>
        <Message texte={msg} />
      </form>
      {versement && <Historique type="salaire" id={versement.id} rafraichir={version} />}
    </Fenetre>
  );
}

function FormPrevision({ prevision, initial, beneficiaires, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    beneficiaire: prevision?.beneficiaire || initial?.beneficiaire || "",
    mois: prevision?.mois || initial?.mois || moisCourant(),
    montant_prevu: prevision?.montant_prevu || "", echeance: prevision?.echeance || "", note: prevision?.note || "",
  }));
  const [msg, setMsg] = useState("");
  const maj = (cle) => (e) => setForm((f) => ({ ...f, [cle]: e.target.value }));
  const envoyer = async (e) => {
    e.preventDefault(); setMsg("");
    try {
      if (prevision?.id) await api.put(`compta/salaires/previsions/${prevision.id}/`, form);
      else await api.post("compta/salaires/previsions/", form);
      onSaved();
    } catch (err) { setMsg(erreurApi(err)); }
  };
  const supprimer = () => qlConfirm("Supprimer cette prévision ? Les versements sont conservés.", async () => {
    try { await api.delete(`compta/salaires/previsions/${prevision.id}/`); onSaved(); }
    catch (err) { qlAlert(erreurApi(err), "error"); }
  }, { danger: true, okLabel: "Supprimer" });
  return (
    <Fenetre titre={prevision?.id ? "Modifier le salaire prévu" : "Salaire prévu pour le mois"} onClose={onClose}>
      <form className="qc-form" onSubmit={envoyer}>
        <label>Bénéficiaire *<input list="qc-beneficiaires-prev" required disabled={Boolean(prevision?.id)} value={form.beneficiaire} onChange={maj("beneficiaire")} /></label>
        <datalist id="qc-beneficiaires-prev">{beneficiaires.map((b) => <option key={b} value={b} />)}</datalist>
        <label>Mois de paie *<input type="month" required disabled={Boolean(prevision?.id)} value={form.mois} onChange={maj("mois")} /></label>
        <label>Montant net prévu (FCFA) *<input type="number" min="1" required value={form.montant_prevu} onChange={maj("montant_prevu")} /></label>
        <label>Échéance<input type="date" value={form.echeance} onChange={maj("echeance")} /></label>
        <label className="qc-full">Note<input value={form.note} onChange={maj("note")} /></label>
        <div className="qc-full qc-note">Plusieurs versements peuvent solder ce montant. Le retard n’est signalé que si une échéance est renseignée et dépassée.</div>
        <div className="qc-actions qc-full">
          <button type="submit" className="qc-btn qc-btn--primary"><i className="bi bi-check2"></i>Enregistrer</button>
          {prevision?.id && <button type="button" className="qc-btn qc-btn--danger" onClick={supprimer}><i className="bi bi-trash"></i>Supprimer la prévision</button>}
        </div>
        <Message texte={msg} />
      </form>
      {prevision?.id && <Historique type="prevision" id={prevision.id} />}
    </Fenetre>
  );
}

function FicheBeneficiaire({ ligne, onClose, onPrevision, onVersement, onNouveauVersement }) {
  const statut = STATUTS_SALAIRE[ligne.statut];
  return (
    <Fenetre titre={`${ligne.beneficiaire} · ${libelleMois(ligne.mois)}`} onClose={onClose}>
      <div className="qc-detailgrid">
        <Ligne label="Montant prévu" valeur={ligne.prevu === null ? "Non renseigné" : fcfa(ligne.prevu)} />
        <Ligne label="Déjà versé" valeur={fcfa(ligne.verse)} classe="qc-amber" />
        <Ligne label="Reste à payer" valeur={ligne.reste === null ? "Non déterminable" : fcfa(ligne.reste)} classe={ligne.reste ? "qc-red" : ""} />
        <Ligne label="Statut" valeur={<Chip ton={ligne.retard ? "red" : statut.ton}>{ligne.retard ? `${statut.label} · en retard` : statut.label}</Chip>} />
        <Ligne label="Échéance" valeur={ligne.prevision?.echeance ? fmtDate(ligne.prevision.echeance) : "Non renseignée"} />
        {ligne.excedent > 0 && <Ligne label="Versé au-delà du prévu" valeur={fcfa(ligne.excedent)} />}
      </div>
      <div className="qc-actions">
        <button type="button" className="qc-btn" onClick={onPrevision}><i className="bi bi-pencil"></i>{ligne.prevision ? "Modifier le prévu" : "Renseigner le prévu"}</button>
        <button type="button" className="qc-btn qc-btn--primary" onClick={onNouveauVersement}><i className="bi bi-plus-lg"></i>Ajouter un versement</button>
      </div>
      <div className="qc-section-title">Versements de ce mois de paie</div>
      {ligne.versements.length === 0 ? <div className="qc-foot">Aucun versement enregistré.</div> : (
        <div className="qc-table-wrap">
          <table className="qc-table">
            <thead><tr><th>Date</th><th className="qc-num">Montant</th><th>Compte</th><th>Justificatif</th><th></th></tr></thead>
            <tbody>{ligne.versements.map((v) => (
              <tr key={v.id}>
                <td>{fmtDate(v.date_paiement)}{v.description && <small>{v.description}</small>}</td>
                <td className="qc-num qc-amber">{fmt(v.montant)}</td>
                <td>{v.compte_label || "—"}</td>
                <td><Justificatif url={v.justificatif_url} etat={v.justificatif_etat} titre={`Justificatif — ${v.beneficiaire}`} /></td>
                <td><button type="button" className="qc-link" onClick={() => onVersement(v)}>Voir</button></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
      {ligne.prevision && <Historique type="prevision" id={ligne.prevision.id} />}
    </Fenetre>
  );
}

export function ComptaSalairesPage() {
  const [periode, setPeriode] = usePeriode();
  const [mois, setMois] = useState(() => (periode.mode === "mois" && periode.mois) || moisCourant());
  const [suivi, setSuivi] = useState(null);
  const [versements, setVersements] = useState(null);
  const [serie, setSerie] = useState([]);
  const [filtres, setFiltres] = useState({ recherche: "", statut: "", justificatif: "", sansMois: false });
  const [fenetre, setFenetre] = useState(null);
  const [focus] = useState(() => lireFocus("compta_salaires"));
  const { label } = bornesPeriode(periode);

  const charger = useCallback(() => {
    api.get(`compta/salaires/suivi/?mois=${mois}`).then((r) => setSuivi(r.data)).catch(() => setSuivi({ lignes: [], totaux: {}, beneficiaires_connus: [] }));
    api.get(`compta/salaires/${parametresPeriode(periode)}`).then((r) => setVersements(r.data.salaires || [])).catch(() => setVersements([]));
    api.get(`compta/dashboard/${parametresPeriode(periode)}`).then((r) => setSerie(r.data.serie_mensuelle || [])).catch(() => setSerie([]));
  }, [mois, periode]);
  useEffect(() => { charger(); }, [charger]);
  useEffect(() => {
    if (!focus) return;
    setFiltres((f) => ({ ...f, statut: focus.statut || "", justificatif: focus.justificatif || "", sansMois: Boolean(focus.sans_mois) }));
  }, [focus]);
  useEffect(() => {
    if (focus?.ouvrir && versements) {
      const v = versements.find((x) => x.id === focus.ouvrir);
      if (v) setFenetre({ type: "versement", versement: v });
    }
  }, [focus, versements]);

  const fermer = () => setFenetre(null);
  const apresEnregistrement = () => { setFenetre(null); charger(); };
  const connus = suivi?.beneficiaires_connus || [];
  const totaux = suivi?.totaux || {};
  const lignes = (suivi?.lignes || []).filter((l) => correspond(filtres.recherche, l.beneficiaire)
    && (!filtres.statut || (filtres.statut === "dus" ? l.reste > 0 : filtres.statut === "retard" ? l.retard : l.statut === filtres.statut)));
  const versementsFiltres = (versements || []).filter((v) => correspond(filtres.recherche, v.beneficiaire, v.description)
    && (!filtres.justificatif || (filtres.justificatif === "_vide" ? !v.justificatif_etat : v.justificatif_etat === filtres.justificatif))
    && (!filtres.sansMois || !v.mois_paie));
  const graphique = {
    labels: serie.map((m) => moisCourt(m.mois)),
    datasets: [{ label: "Salaires versés", data: serie.map((m) => m.salaires / 1000), borderColor: COULEURS.salaires, backgroundColor: COULEURS.salaires, tension: .25, pointRadius: 4 }],
  };

  return (
    <div className="qc">
      <Entete titre="Salaires" sousTitre="Suivre les bénéficiaires, les versements et les sommes restant dues.">
        <PeriodeBar periode={periode} onChange={setPeriode} />
      </Entete>
      {suivi === null ? <Chargement /> : (
        <>
          <div className="qc-kpis">
            <Kpi label="Montant prévu" value={fmt(totaux.prevu)} ton={COULEURS.navy} sub={`FCFA · ${suivi.mois_label}`} />
            <Kpi label="Salaires versés" value={fmt(totaux.verse)} ton={COULEURS.salaires} sub={`FCFA · mois de paie ${suivi.mois_label}`} />
            <Kpi label="Reste à payer" value={fmt(totaux.reste)} ton={COULEURS.depenses}
              sub={totaux.sans_prevision ? `FCFA · ${totaux.sans_prevision} bénéficiaire(s) sans prévision non comptés` : "FCFA · prévu moins versé"} />
            <Kpi label="Bénéficiaires à solder" value={totaux.a_solder || 0} ton={COULEURS.navy} sub={`Sur ${totaux.beneficiaires || 0} bénéficiaire(s)`} />
          </div>
          <div className="qc-toolbar">
            <input className="qc-search" placeholder="Rechercher un bénéficiaire…" value={filtres.recherche}
              onChange={(e) => setFiltres((f) => ({ ...f, recherche: e.target.value }))} aria-label="Rechercher" />
            <label className="qc-chip" style={{ padding: "6px 10px" }}>Mois de paie :
              <input type="month" value={mois} onChange={(e) => setMois(e.target.value || moisCourant())} style={{ border: 0, padding: 0, background: "transparent", marginLeft: 6 }} />
            </label>
            <select value={filtres.statut} onChange={(e) => setFiltres((f) => ({ ...f, statut: e.target.value }))} aria-label="Statut">
              <option value="">Tous les statuts</option>
              <option value="dus">Restant dus</option>
              <option value="retard">En retard</option>
              <option value="paye">Payé</option>
              <option value="partiel">Partiellement payé</option>
              <option value="a_payer">À payer</option>
              <option value="non_prevu">Prévision non renseignée</option>
            </select>
            <span className="qc-spacer" />
            <button type="button" className="qc-btn" onClick={() => setFenetre({ type: "prevision", initial: { mois } })}><i className="bi bi-calendar-plus"></i>Salaire prévu</button>
            <button type="button" className="qc-btn qc-btn--primary" onClick={() => setFenetre({ type: "versement", initial: { mois_paie: mois } })}><i className="bi bi-plus-lg"></i>Ajouter</button>
          </div>

          <div className="qc-card" style={{ marginBottom: 16 }}>
            <h3>Suivi des bénéficiaires · {suivi.mois_label}</h3>
            {lignes.length === 0 ? <div className="qc-empty">Aucun bénéficiaire pour ce mois de paie. Renseignez un salaire prévu ou un versement.</div> : (
              <div className="qc-table-wrap">
                <table className="qc-table">
                  <thead><tr><th>Bénéficiaire</th><th className="qc-num">Prévu</th><th className="qc-num">Versé</th><th className="qc-num">Restant</th><th>Échéance</th><th>Statut</th><th>Détail</th></tr></thead>
                  <tbody>{lignes.map((l) => {
                    const s = STATUTS_SALAIRE[l.statut];
                    return (
                      <tr key={`${l.mois}-${l.beneficiaire}`}>
                        <td>{l.beneficiaire}<small>{l.versements.length} versement(s)</small></td>
                        <td className="qc-num">{l.prevu === null ? "—" : fmt(l.prevu)}</td>
                        <td className="qc-num qc-amber">{fmt(l.verse)}</td>
                        <td className="qc-num qc-red">{l.reste === null ? "—" : fmt(l.reste)}</td>
                        <td>{l.prevision?.echeance ? fmtDate(l.prevision.echeance) : "—"}</td>
                        <td><Chip ton={l.retard ? "red" : s.ton}>{l.retard ? `${s.label} · en retard` : s.label}</Chip></td>
                        <td><button type="button" className="qc-link" onClick={() => setFenetre({ type: "fiche", ligne: l })}>Voir</button></td>
                      </tr>
                    );
                  })}</tbody>
                </table>
              </div>
            )}
            <div className="qc-foot">Sans salaire prévu, le reste à payer n’est pas calculé. Statut : à payer, partiellement payé ou payé.</div>
          </div>

          <div className="qc-cols qc-cols--half">
            <div className="qc-card">
              <h3>Versements par mois</h3>
              <div className="qc-chart">
                <Line data={graphique} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false },
                  tooltip: { callbacks: { label: (c) => fcfa(c.parsed.y * 1000) } } },
                scales: { y: { beginAtZero: true, title: { display: true, text: "Milliers de FCFA" }, grid: { color: "#eef2f6" } }, x: { grid: { display: false } } } }} />
              </div>
            </div>
            <div className="qc-card">
              <h3>Une fiche par bénéficiaire</h3>
              <p className="qc-subtitle" style={{ fontSize: ".9rem" }}>Montant prévu, échéance et versements successifs du mois, réunis dans la fiche « Voir ».</p>
              <div className="qc-steps"><Chip ton="red">À payer</Chip><Chip ton="amber">Partiellement payé</Chip><Chip ton="green">Payé</Chip></div>
              {suivi.versements_sans_mois > 0 && (
                <Ligne label="Versements sans mois de paie reconnu" valeur={suivi.versements_sans_mois}
                  onClick={() => setFiltres((f) => ({ ...f, sansMois: true }))} />
              )}
              <div className="qc-note">Salaire restant dû ≠ solde disponible de l’entreprise.</div>
            </div>
          </div>

          <div className="qc-card">
            <h3>Versements de la période <span className="qc-foot" style={{ marginLeft: "auto", marginTop: 0, fontWeight: 400 }}>{label} · date effective · {fcfa(sommer(versementsFiltres, "montant"))}</span></h3>
            <div className="qc-toolbar" style={{ marginBottom: 8 }}>
              <select value={filtres.justificatif} onChange={(e) => setFiltres((f) => ({ ...f, justificatif: e.target.value }))} aria-label="Justificatif">
                <option value="">Tous les justificatifs</option>
                {ETATS_JUSTIFICATIF.map(([cle, lib]) => <option key={cle} value={cle}>{lib}</option>)}
                <option value="_vide">Non renseigné</option>
              </select>
              {filtres.sansMois && <button type="button" className="qc-btn qc-btn--sm" onClick={() => setFiltres((f) => ({ ...f, sansMois: false }))}>Sans mois de paie ✕</button>}
            </div>
            {versements === null ? <Chargement /> : versementsFiltres.length === 0 ? <div className="qc-empty">Aucun versement sur la période.</div> : (
              <div className="qc-table-wrap">
                <table className="qc-table">
                  <thead><tr><th>Date</th><th>Bénéficiaire</th><th>Mois de paie</th><th className="qc-num">Montant</th><th>Justificatif</th><th>Détail</th></tr></thead>
                  <tbody>{versementsFiltres.map((v) => (
                    <tr key={v.id}>
                      <td>{fmtDate(v.date_paiement)}</td>
                      <td>{v.beneficiaire}{v.description && <small>{v.description}</small>}</td>
                      <td>{v.mois_paie_label || <Chip ton="grey">{v.mois_concerne || "Non reconnu"}</Chip>}</td>
                      <td className="qc-num qc-amber">{fmt(v.montant)}</td>
                      <td><Justificatif url={v.justificatif_url} etat={v.justificatif_etat} titre={`Justificatif — ${v.beneficiaire}`} /></td>
                      <td><button type="button" className="qc-link" onClick={() => setFenetre({ type: "versement", versement: v })}>Voir</button></td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
      {fenetre?.type === "fiche" && (
        <FicheBeneficiaire ligne={fenetre.ligne} onClose={fermer}
          onPrevision={() => setFenetre({ type: "prevision", prevision: fenetre.ligne.prevision ? { ...fenetre.ligne.prevision, beneficiaire: fenetre.ligne.beneficiaire, mois: fenetre.ligne.mois } : null, initial: { beneficiaire: fenetre.ligne.beneficiaire, mois: fenetre.ligne.mois } })}
          onVersement={(v) => setFenetre({ type: "versement", versement: v })}
          onNouveauVersement={() => setFenetre({ type: "versement", initial: { beneficiaire: fenetre.ligne.beneficiaire, mois_paie: fenetre.ligne.mois } })} />
      )}
      {fenetre?.type === "versement" && (
        <FormVersement versement={fenetre.versement} initial={fenetre.initial} beneficiaires={connus} onClose={fermer} onSaved={apresEnregistrement} />
      )}
      {fenetre?.type === "prevision" && (
        <FormPrevision prevision={fenetre.prevision} initial={fenetre.initial} beneficiaires={connus} onClose={fermer} onSaved={apresEnregistrement} />
      )}
    </div>
  );
}

// ── Dépenses ────────────────────────────────────────────────────────────────

function FormDepense({ depense, peutApprouver, onClose, onSaved }) {
  const [form, setForm] = useState(() => depense ? {
    titre: depense.titre, montant: depense.montant, date_depense: depense.date_depense, categorie: depense.categorie,
    fournisseur: depense.fournisseur || "", compte: depense.compte || "", description: depense.description || "",
    justificatif_etat: depense.justificatif_etat || "",
  } : {
    titre: "", montant: "", date_depense: new Date().toISOString().slice(0, 10), categorie: "autre", fournisseur: "",
    compte: "", description: "", justificatif_etat: "", statut: peutApprouver ? "payee" : "a_valider",
  });
  const [fichier, setFichier] = useState(null);
  const [msg, setMsg] = useState("");
  const maj = (cle) => (e) => setForm((f) => ({ ...f, [cle]: e.target.value }));
  const statutsPossibles = peutApprouver ? STATUTS_DEPENSE : STATUTS_DEPENSE.filter(([cle]) => ["brouillon", "a_valider"].includes(cle));

  const envoyer = async (e) => {
    e.preventDefault(); setMsg("");
    try {
      if (depense) {
        const r = await api.put(`compta/depenses/${depense.id}/`, formulaire(form, fichier), MULTIPART);
        if (r.data.statut === "a_valider" && depense.statut === "approuvee") qlAlert("Montant modifié : la dépense repart en validation.", "info");
      } else {
        await api.post("compta/depenses/", formulaire(form, fichier), MULTIPART);
      }
      onSaved();
    } catch (err) { setMsg(erreurApi(err)); }
  };

  return (
    <Fenetre titre={depense ? "Modifier la dépense" : "Ajouter une dépense"} onClose={onClose}>
      <form className="qc-form" onSubmit={envoyer}>
        <label>Titre *<input required value={form.titre} onChange={maj("titre")} /></label>
        <label>Montant (FCFA) *<input type="number" min="1" required value={form.montant} onChange={maj("montant")} /></label>
        <label>Date *<input type="date" required value={form.date_depense} onChange={maj("date_depense")} /></label>
        <label>Catégorie
          <select value={form.categorie} onChange={maj("categorie")}>
            {CATEGORIES.map(([cle, lib]) => <option key={cle} value={cle}>{lib}</option>)}
          </select>
        </label>
        <label>Fournisseur / bénéficiaire<input value={form.fournisseur} onChange={maj("fournisseur")} /></label>
        <label>Compte de paiement
          <select value={form.compte} onChange={maj("compte")}>
            <option value="">Non renseigné</option>
            {COMPTES.map(([cle, lib]) => <option key={cle} value={cle}>{lib}</option>)}
          </select>
        </label>
        {!depense && (
          <label>Statut
            <select value={form.statut} onChange={maj("statut")}>
              {statutsPossibles.map(([cle, lib]) => <option key={cle} value={cle}>{lib}</option>)}
            </select>
          </label>
        )}
        <label className={depense ? "qc-full" : ""}>Description<input value={form.description} onChange={maj("description")} /></label>
        <ChampsJustificatif etat={form.justificatif_etat} existant={Boolean(depense?.justificatif_url)} onEtat={(v) => setForm((f) => ({ ...f, justificatif_etat: v }))} onFichier={setFichier} />
        {!peutApprouver && !depense && <div className="qc-full qc-note">« À valider » envoie la demande d’approbation dans la messagerie des super admins : le CEO à partir de 20 000 FCFA, n’importe quel super admin en dessous.</div>}
        <div className="qc-actions qc-full">
          <button type="submit" className="qc-btn qc-btn--primary"><i className="bi bi-check2"></i>Enregistrer</button>
          <button type="button" className="qc-btn" onClick={onClose}>Annuler</button>
        </div>
        <Message texte={msg} />
      </form>
    </Fenetre>
  );
}

function FicheDepense({ depense, peutApprouver, onClose, onModifier, onChange }) {
  const [courante, setCourante] = useState(depense);
  const [version, setVersion] = useState(0);
  const [msg, setMsg] = useState("");
  const [datePaiement, setDatePaiement] = useState(new Date().toISOString().slice(0, 10));

  const action = async (nom, extra = {}) => {
    setMsg("");
    try {
      const r = await api.post(`compta/depenses/${courante.id}/statut/`, { action: nom, ...extra });
      setCourante(r.data); setVersion((v) => v + 1); onChange();
    } catch (err) { setMsg(erreurApi(err)); }
  };
  const supprimer = () => qlConfirm("Supprimer cette dépense ?", async () => {
    try { await api.delete(`compta/depenses/${courante.id}/`); onChange(); onClose(); }
    catch (err) { qlAlert(erreurApi(err), "error"); }
  }, { danger: true, okLabel: "Supprimer" });
  const s = courante.statut;
  const peutPayer = s === "approuvee" || (peutApprouver && s !== "payee");

  return (
    <Fenetre titre="Détail de la dépense" onClose={onClose}>
      <div className="qc-detailgrid">
        <Ligne label="Titre" valeur={courante.titre} />
        <Ligne label="Montant" valeur={fcfa(courante.montant)} classe="qc-red" />
        <Ligne label={s === "payee" ? "Date du paiement" : "Date"} valeur={fmtDate(courante.date_depense)} />
        <Ligne label="Catégorie" valeur={courante.categorie_label} />
        <Ligne label="Fournisseur / bénéficiaire" valeur={courante.fournisseur || "—"} />
        <Ligne label="Compte de paiement" valeur={courante.compte_label || "—"} />
        <Ligne label="Statut" valeur={<Chip ton={TON_STATUT_DEPENSE[s]}>{courante.statut_label}</Chip>} />
        <Ligne label="Justificatif" valeur={<Justificatif url={courante.justificatif_url} etat={courante.justificatif_etat} titre={`Justificatif — ${courante.titre}`} />} />
        {courante.valide_par && <Ligne label="Approuvée par" valeur={`${courante.valide_par}${courante.valide_le ? `, ${fmtDate(courante.valide_le)}` : ""}`} />}
      </div>
      {courante.description && <p className="qc-subtitle" style={{ marginTop: 10 }}>{courante.description}</p>}

      <div className="qc-section-title">Circuit de validation</div>
      <div className="qc-steps">
        {STATUTS_DEPENSE.map(([cle, lib], i) => (
          <React.Fragment key={cle}>
            {i > 0 && <span className="qc-foot" style={{ marginTop: 0 }}>→</span>}
            <span className="qc-step" style={cle === s ? { background: "#203e60", color: "#fff" } : undefined}>{lib}</span>
          </React.Fragment>
        ))}
      </div>
      <div className="qc-actions">
        {s === "brouillon" && <button type="button" className="qc-btn" onClick={() => action("soumettre")}><i className="bi bi-send"></i>Soumettre à validation</button>}
        {peutApprouver && ["brouillon", "a_valider"].includes(s) && <button type="button" className="qc-btn qc-btn--primary" onClick={() => action("approuver")}><i className="bi bi-check2-circle"></i>Approuver</button>}
        {peutApprouver && ["a_valider", "approuvee"].includes(s) && <button type="button" className="qc-btn qc-btn--danger" onClick={() => action("rejeter")}><i className="bi bi-arrow-counterclockwise"></i>Renvoyer en brouillon</button>}
        {peutPayer && (
          <>
            <input type="date" value={datePaiement} onChange={(e) => setDatePaiement(e.target.value)} aria-label="Date du paiement"
              style={{ border: "1px solid #e0e7ee", borderRadius: 8, padding: "8px 10px" }} />
            <button type="button" className="qc-btn qc-btn--success" onClick={() => action("payer", { date_paiement: datePaiement })}><i className="bi bi-cash"></i>Marquer payée</button>
          </>
        )}
      </div>
      {!peutApprouver && s === "a_valider" && <div className="qc-foot">{courante.montant >= 20000
        ? "En attente d’approbation par le CEO (dépense ≥ 20 000 FCFA)."
        : "En attente d’approbation par un super admin, depuis sa messagerie."}</div>}
      <div className="qc-actions">
        <button type="button" className="qc-btn" onClick={() => onModifier(courante)}><i className="bi bi-pencil"></i>Modifier</button>
        <button type="button" className="qc-btn qc-btn--danger" onClick={supprimer}><i className="bi bi-trash"></i>Supprimer</button>
      </div>
      <Message texte={msg} />
      <Historique type="depense" id={courante.id} rafraichir={version} />
    </Fenetre>
  );
}

export function ComptaDepensesPage() {
  const [periode, setPeriode] = usePeriode();
  const [donnees, setDonnees] = useState(null);
  const [solde, setSolde] = useState(null);
  const [filtres, setFiltres] = useState({ recherche: "", categorie: "", statut: "", justificatif: "" });
  const [fenetre, setFenetre] = useState(null);
  const [pdf, setPdf] = useState(false);
  const [focus] = useState(() => lireFocus("compta_depenses"));
  const { label } = bornesPeriode(periode);

  const charger = useCallback(() => {
    api.get(`compta/depenses/${parametresPeriode(periode)}`).then((r) => setDonnees(r.data)).catch(() => setDonnees({ depenses: [], peut_approuver: false }));
    api.get(`compta/dashboard/${parametresPeriode(periode)}`).then((r) => setSolde(r.data.solde)).catch(() => setSolde(null));
  }, [periode]);
  useEffect(() => { charger(); }, [charger]);
  useEffect(() => {
    if (focus) setFiltres((f) => ({ ...f, statut: focus.statut || "", justificatif: focus.justificatif || "" }));
  }, [focus]);
  useEffect(() => {
    if (focus?.ouvrir && donnees) {
      const d = donnees.depenses.find((x) => x.id === focus.ouvrir);
      if (d) setFenetre({ type: "fiche", depense: d });
    }
  }, [focus, donnees]);

  const telechargerPdf = (imprimer = false) => {
    setPdf(true);
    const nom = `depenses_${new Date().toISOString().slice(0, 10)}.pdf`;
    api.get(`compta/depenses/pdf/${parametresPeriode(periode)}`, { responseType: "blob" })
      .then((r) => (imprimer ? imprimerDocument({ data: r.data, filename: nom }) : pdfOuvrir(r.data, nom)))
      .catch(() => qlAlert("Erreur lors de la génération du PDF.", "error"))
      .finally(() => setPdf(false));
  };

  const liste = donnees?.depenses || [];
  const peutApprouver = Boolean(donnees?.peut_approuver);
  const payees = liste.filter((d) => d.statut === "payee");
  const filtrees = liste.filter((d) => (!filtres.categorie || d.categorie === filtres.categorie)
    && (!filtres.statut || d.statut === filtres.statut)
    && (!filtres.justificatif || (filtres.justificatif === "_vide" ? !d.justificatif_etat : d.justificatif_etat === filtres.justificatif))
    && correspond(filtres.recherche, d.titre, d.description, d.fournisseur, d.categorie_label));
  const parStatut = Object.fromEntries(STATUTS_DEPENSE.map(([cle]) => [cle, liste.filter((d) => d.statut === cle)]));
  const aFournir = liste.filter((d) => d.justificatif_etat === "manquant");

  return (
    <div className="qc">
      <Entete titre="Dépenses" sousTitre="Suivre les sorties, leurs catégories et les pièces justificatives.">
        <PeriodeBar periode={periode} onChange={setPeriode} />
      </Entete>
      {donnees === null ? <Chargement /> : (
        <>
          <div className="qc-kpis">
            <Kpi label="Dépenses payées" value={fmt(sommer(payees, "montant"))} ton={COULEURS.depenses} sub={`FCFA · hors salaires · ${label}`} />
            <Kpi label="Catégories" value={new Set(payees.map((d) => d.categorie)).size} ton={COULEURS.navy} sub="Sur la période" />
            <Kpi label="Justificatifs à fournir" value={aFournir.length} ton="#d68600" sub={aFournir.length ? `${fmt(sommer(aFournir, "montant"))} FCFA à documenter` : "Aucune pièce marquée à fournir"} />
            <Kpi label="Solde des opérations" value={solde === null ? "—" : fmt(solde)} ton={solde < 0 ? COULEURS.depenses : COULEURS.navy} sub="FCFA · après sorties" />
          </div>
          <div className="qc-toolbar">
            <input className="qc-search" placeholder="Rechercher une dépense…" value={filtres.recherche}
              onChange={(e) => setFiltres((f) => ({ ...f, recherche: e.target.value }))} aria-label="Rechercher" />
            <select value={filtres.categorie} onChange={(e) => setFiltres((f) => ({ ...f, categorie: e.target.value }))} aria-label="Catégorie">
              <option value="">Toutes les catégories</option>
              {CATEGORIES.map(([cle, lib]) => <option key={cle} value={cle}>{lib}</option>)}
            </select>
            <select value={filtres.statut} onChange={(e) => setFiltres((f) => ({ ...f, statut: e.target.value }))} aria-label="Statut">
              <option value="">Tous les statuts</option>
              {STATUTS_DEPENSE.map(([cle, lib]) => <option key={cle} value={cle}>{lib}</option>)}
            </select>
            <select value={filtres.justificatif} onChange={(e) => setFiltres((f) => ({ ...f, justificatif: e.target.value }))} aria-label="Justificatif">
              <option value="">Tous les justificatifs</option>
              {ETATS_JUSTIFICATIF.map(([cle, lib]) => <option key={cle} value={cle}>{lib}</option>)}
              <option value="_vide">Non renseigné</option>
            </select>
            <span className="qc-spacer" />
            <button type="button" className="qc-btn" onClick={() => telechargerPdf(false)} disabled={pdf}><i className="bi bi-file-earmark-pdf"></i>{pdf ? "Génération…" : "Télécharger PDF"}</button>
            <button type="button" className="qc-btn" onClick={() => telechargerPdf(true)} disabled={pdf}><i className="bi bi-printer"></i>Imprimer</button>
            <button type="button" className="qc-btn qc-btn--primary" onClick={() => setFenetre({ type: "form" })}><i className="bi bi-plus-lg"></i>Ajouter</button>
          </div>
          <div className="qc-card" style={{ marginBottom: 16 }}>
            <h3>Dépenses hors salaires <span className="qc-foot" style={{ marginLeft: "auto", marginTop: 0, fontWeight: 400 }}>{filtrees.length} ligne(s)</span></h3>
            {filtrees.length === 0 ? <div className="qc-empty">Aucune dépense correspondante.</div> : (
              <div className="qc-table-wrap">
                <table className="qc-table">
                  <thead><tr><th>Titre</th><th className="qc-num">Montant</th><th>Date</th><th>Catégorie</th><th>Statut</th><th>Justificatif</th><th>Détail</th></tr></thead>
                  <tbody>{filtrees.map((d) => (
                    <tr key={d.id}>
                      <td>{d.titre}{d.fournisseur && <small>{d.fournisseur}</small>}</td>
                      <td className="qc-num qc-red">{fmt(d.montant)}</td>
                      <td>{fmtDate(d.date_depense)}</td>
                      <td><Chip ton="amber">{d.categorie_label}</Chip></td>
                      <td><Chip ton={TON_STATUT_DEPENSE[d.statut]}>{d.statut_label}</Chip></td>
                      <td><Justificatif url={d.justificatif_url} etat={d.justificatif_etat} titre={`Justificatif — ${d.titre}`} /></td>
                      <td><button type="button" className="qc-link" onClick={() => setFenetre({ type: "fiche", depense: d })}>Voir</button></td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            )}
            <div className="qc-foot">Seules les dépenses « Payée » entrent dans les totaux et l’export PDF.</div>
          </div>
          <div className="qc-cols qc-cols--half">
            <div className="qc-card">
              <h3>Répartition par catégorie</h3>
              <Repartition couleur={COULEURS.depenses} lignes={repartir(payees, "categorie", "montant", LIB_CATEGORIES)} vide="Aucune dépense payée sur la période." />
            </div>
            <div className="qc-card">
              <h3>Validation des dépenses</h3>
              <div className="qc-steps">
                {STATUTS_DEPENSE.map(([cle, lib], i) => (
                  <React.Fragment key={cle}>
                    {i > 0 && <span className="qc-foot" style={{ marginTop: 0 }}>→</span>}
                    <button type="button" className="qc-step" style={{ border: 0, cursor: "pointer" }} onClick={() => setFiltres((f) => ({ ...f, statut: cle }))}>
                      {lib}<small>{parStatut[cle].length} · {fmt(sommer(parStatut[cle], "montant"))} FCFA</small>
                    </button>
                  </React.Fragment>
                ))}
              </div>
              <p className="qc-subtitle" style={{ fontSize: ".9rem" }}>Une demande en attente ou approuvée reste distincte des dépenses effectivement payées : elle ne modifie pas les décaissements.</p>
              <div className="qc-foot">{peutApprouver ? "Vous pouvez approuver les demandes, ici ou depuis la messagerie." : "Les demandes partent dans la messagerie des super admins : le principal approuve à partir de 20 000 FCFA, n’importe quel super admin en dessous."}</div>
            </div>
          </div>
        </>
      )}
      {fenetre?.type === "form" && (
        <FormDepense depense={fenetre.depense} peutApprouver={peutApprouver} onClose={() => setFenetre(null)} onSaved={() => { setFenetre(null); charger(); }} />
      )}
      {fenetre?.type === "fiche" && (
        <FicheDepense depense={fenetre.depense} peutApprouver={peutApprouver} onClose={() => setFenetre(null)} onChange={charger}
          onModifier={(d) => setFenetre({ type: "form", depense: d })} />
      )}
    </div>
  );
}

